import { useState } from 'react';
import { useAddKitNote } from '@/application/usecases/useKits';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import styles from './AddNote.module.css';

/**
 * Writing a free note on a case, under the notes list. Section 3.5 of the
 * request of 16 September 2026.
 *
 * On the job view and at Final Check, where somebody working the case has
 * something to say. Not on the archive record: a delivered case does not take
 * new notes.
 *
 * The box sends text and nothing else; the server stamps what kind of note it is
 * and where it was written from, because a screen that chose those could choose
 * them wrong.
 */
export function AddNote({ kitId }: { kitId: string }) {
  const add = useAddKitNote();
  const [text, setText] = useState('');

  function save() {
    const trimmed = text.trim();
    if (trimmed === '') return;
    add.mutate(
      { kitId, text: trimmed },
      {
        onSuccess: () => setText(''),
        onError: (error: Error) => toast(failureWords(error)),
      },
    );
  }

  return (
    <div className={styles.add}>
      <textarea
        aria-label={strings.notes.add}
        placeholder={strings.notes.placeholder}
        value={text}
        disabled={add.isPending}
        onChange={(event) => setText(event.target.value)}
      />
      <div className={styles.row}>
        <Button onClick={save} disabled={add.isPending || text.trim() === ''}>
          {add.isPending ? strings.notes.adding : strings.notes.add}
        </Button>
      </div>
    </div>
  );
}
